import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageUpload } from '@/components/ImageUpload';

import { API_URL } from '@/lib/api';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    product_code: '',
    category_id: '',
    image_url: '',
    description: '',
    gst_rate: 0,
    status: 'active'
  });

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      const list = (response.data || []).map(c => ({
        id: c.id,
        name: c.name,
        status: c.status === true ? 'active' : 'inactive'
      }));
      setCategories(list.filter(c => c.status === 'active'));
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API_URL}/products/${id}`);
      const d = res.data;
      setFormData({
        name: d.name || '',
        product_code: d.productCode || '',
        category_id: d.categoryId || '',
        image_url: d.image || '',
        description: d.description || '',
        gst_rate: d.gstRate ?? 0,
        status: d.status === true ? 'active' : 'inactive'
      });
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/products');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        productCode: formData.product_code,
        categoryId: formData.category_id,
        description: formData.description,
        image: formData.image_url,
        gstRate: Number(formData.gst_rate),
        status: formData.status === 'active',
      };
      if (isEdit) {
        await axios.put(`${API_URL}/products/${id}`, payload);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/products`, payload);
        toast.success('Product created successfully');
      }
      navigate('/products');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save product';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
      </div>
    );
  }

  return (
    <div data-testid="product-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/products')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="page-title">{isEdit ? 'Edit Product' : 'Create Product'}</h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Update product information' : 'Add a new product to your catalog'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Product Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="form-label">Product Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter product name"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="product_code" className="form-label">Product Code *</Label>
                  <Input
                    id="product_code"
                    name="product_code"
                    value={formData.product_code}
                    onChange={handleChange}
                    placeholder="e.g., PRD001"
                    required
                    data-testid="product-code-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="category_id" className="form-label">Category *</Label>
                <Select 
                  value={formData.category_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* GST Rate */}
              <div>
                <Label htmlFor="gst_rate" className="form-label">GST Rate</Label>
                <Select
                  value={String(formData.gst_rate)}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, gst_rate: Number(v) }))}
                >
                  <SelectTrigger data-testid="gst-rate-select">
                    <SelectValue placeholder="Select GST rate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% — Exempt</SelectItem>
                    <SelectItem value="5">5% — Essential goods</SelectItem>
                    <SelectItem value="12">12% — Standard goods</SelectItem>
                    <SelectItem value="18">18% — FMCG / Electronics</SelectItem>
                    <SelectItem value="28">28% — Luxury goods</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Applied on base price at checkout. Not shown on product listing.</p>
              </div>

              <div data-testid="image-url-input">
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                  folder="products"
                  label="Image"
                  placeholder="Upload or paste URL"
                />
              </div>

              <div>
                <Label htmlFor="description" className="form-label">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Product description"
                  rows={4}
                  data-testid="description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Product Active</p>
                  <p className="text-sm text-slate-500">
                    {formData.status === 'active' ? 'Product is available' : 'Product is hidden'}
                  </p>
                </div>
                <Switch
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, status: checked ? 'active' : 'inactive' }))
                  }
                  data-testid="status-toggle"
                />
              </div>

              {isEdit && (
                <div className="mt-6 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/products/${id}/variants`)}
                    data-testid="manage-variants-btn"
                  >
                    Manage Variants
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 mt-8">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/products')}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-purple-950 hover:bg-purple-900"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              isEdit ? 'Update Product' : 'Create Product'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
